import React from 'react';
import type { RoomState } from '../types';

interface ShareControlsProps {
  roomKey: string | null;
  roomName: string | null;
  roomState: RoomState;
  isConnected: boolean;
  onStartSharing: (name?: string) => void;
  onStopSharing: () => void;
  onDeleteRoom: (key: string) => void;
}

export const ShareControls: React.FC<ShareControlsProps> = ({
  roomKey,
  roomName,
  roomState,
  isConnected,
  onStartSharing,
  onStopSharing,
  onDeleteRoom,
}) => {
  const shareUrl = roomKey ? `${window.location.origin}/?room=${roomKey}` : null;
  
  // Derived: is this a stopped state?
  const isStopped = roomState.type === 'sharedStopped';

  const copyToClipboard = async () => {
    if (shareUrl) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Deel-URL gekopieerd naar klembord!');
      } catch (err) {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = shareUrl;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        alert('Deel-URL gekopieerd naar klembord!');
      }
    }
  };

  // Render based on room state
  switch (roomState.type) {
    case 'unselected':
      // This case shouldn't happen as parent only renders when roomKey exists
      return null;
      
    case 'selected':
      // Lokale kamer, niet gedeeld - alleen "Start Delen" knop zichtbaar
      return (
        <div className="share-controls" style={{ margin: '16px 0', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px' }}>
                {roomName || 'Naamloos'}
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '0.85em', color: '#666' }}>
                Lokale JSON - Niet gedeeld
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => onStartSharing()}
                style={{ padding: '8px 16px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Start Delen
              </button>
            </div>
          </div>
        </div>
      );
      
    case 'sharingStarting':
      // Begint met delen - toon laadstatus
      return (
        <div className="share-controls" style={{ margin: '16px 0', padding: '16px', background: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px' }}>
                {roomName || 'Naamloos'}
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '0.85em', color: '#856404' }}>
                Starten met delen...
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                disabled
                style={{ padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'not-allowed' }}
              >
                Starten...
              </button>
            </div>
          </div>
        </div>
      );
      
    case 'sharing':
      // Actief aan het delen - "Stop Delen" knop zichtbaar
      return (
        <div className="share-controls" style={{ margin: '16px 0', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px' }}>
                {roomName}
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '0.85em', color: '#666' }}>
                Kamer: {roomKey?.substring(0, 8)}...
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={copyToClipboard}
                disabled={!shareUrl}
                style={{ padding: '8px 16px', background: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                title="Kopieer deel-URL"
              >
                Kopieer URL
              </button>
              <button
                onClick={onStopSharing}
                style={{ padding: '8px 16px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Stop Delen
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.9em' }}>
            <span>
              Status: {isConnected ? '✅ Verbonden' : '❌ Niet verbonden'}
            </span>
            {shareUrl && (
              <span style={{ fontFamily: 'monospace', fontSize: '0.85em', color: '#666', wordBreak: 'break-all' }}>
                {shareUrl}
              </span>
            )}
          </div>

          {!isConnected && (
            <p style={{ marginTop: '8px', color: '#856404', background: '#fff3cd', padding: '8px', borderRadius: '4px', fontSize: '0.85em' }}>
              Wachten op verbinding... Andere gebruikers kunnen verbinden zodra de WebSocket actief is.
            </p>
          )}
        </div>
      );
      
    case 'sharingStopping':
      // Stoppen met delen - toon laadstatus
      return (
        <div className="share-controls" style={{ margin: '16px 0', padding: '16px', background: '#f8d7da', borderRadius: '8px', border: '1px solid #f5c6cb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px' }}>
                {roomName}
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '0.85em', color: '#721c24' }}>
                Stoppen met delen...
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                disabled
                style={{ padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'not-allowed' }}
              >
                Stoppen...
              </button>
            </div>
          </div>
        </div>
      );
      
    case 'sharedStarting':
      // Verbinden met gedeelde kamer - toon laadstatus
      return (
        <div className="share-controls" style={{ margin: '16px 0', padding: '16px', background: '#e7f3ff', borderRadius: '8px', border: '1px solid #b3d9ff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px' }}>
                {roomName || 'Gedeelde JSON'}
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '0.85em', color: '#004085' }}>
                Kamer: {roomKey?.substring(0, 8)}...
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                disabled
                style={{ padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'not-allowed' }}
              >
                Verbinden...
              </button>
            </div>
          </div>
          <p style={{ marginTop: '8px', color: '#004085', fontSize: '0.85em' }}>
            Verbinden met gedeelde JSON... Wijzigingen worden gesynchroniseerd zodra verbonden.
          </p>
        </div>
      );
      
    case 'shared':
      // Verbonden met gedeelde kamer - uitgeschakelde "Gedeeld" knop zichtbaar
      return (
        <div className="share-controls" style={{ margin: '16px 0', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px' }}>
                {roomName || 'Gedeelde JSON'}
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '0.85em', color: '#666' }}>
                Kamer: {roomKey?.substring(0, 8)}...
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={copyToClipboard}
                disabled={!shareUrl || isStopped}
                style={{ padding: '8px 16px', background: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: isStopped ? 'not-allowed' : 'pointer' }}
                title={isStopped ? "Delen is gestopt" : "Kopieer deel-URL"}
              >
                Kopieer URL
              </button>
              <button
                disabled
                style={{ padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'not-allowed' }}
                title="Deze JSON wordt gedeeld door iemand anders"
              >
                Gedeeld
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.9em' }}>
            <span>
              Status: {isConnected ? '✅ Verbonden' : '❌ Niet verbonden'}
            </span>
            {shareUrl && !isStopped && (
              <span style={{ fontFamily: 'monospace', fontSize: '0.85em', color: '#666', wordBreak: 'break-all' }}>
                {shareUrl}
              </span>
            )}
          </div>

          {!isConnected && (
            <p style={{ marginTop: '8px', color: '#856404', background: '#fff3cd', padding: '8px', borderRadius: '4px', fontSize: '0.85em' }}>
              Verbinden met gedeelde JSON... Wijzigingen worden gesynchroniseerd zodra verbonden.
            </p>
          )}
          
          {isStopped && (
            <p style={{ marginTop: '8px', color: '#721c24', background: '#f8d7da', padding: '8px', borderRadius: '4px', fontSize: '0.85em', border: '1px solid #f5c6cb' }}>
              <strong>Delen gestopt</strong> - De eigenaar heeft het delen beëindigd. U kunt deze JSON niet meer bewerken.
            </p>
          )}
        </div>
      );
      
    case 'sharedStopped':
      // Delen is gestopt door initiator - toon Wis knop
      return (
        <div className="share-controls" style={{ margin: '16px 0', padding: '16px', background: '#f8d7da', borderRadius: '8px', border: '1px solid #f5c6cb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px' }}>
                {roomName || 'Gedeelde JSON'}
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '0.85em', color: '#721c24' }}>
                Kamer: {roomKey?.substring(0, 8)}...
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {roomKey && (
                <button
                  onClick={() => {
                    onDeleteRoom(roomKey);
                    // Ook kamer uit URL verwijderen
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.delete('room');
                    window.history.replaceState({}, '', newUrl);
                  }}
                  style={{ padding: '8px 16px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  title="Verwijder deze entry"
                >
                  Wis
                </button>
              )}
            </div>
          </div>
          <p style={{ marginTop: '8px', color: '#721c24', background: '#f8d7da', padding: '8px', borderRadius: '4px', fontSize: '0.85em', border: '1px solid #f5c6cb' }}>
            <strong>Delen gestopt</strong> - De eigenaar heeft het delen beëindigd. U kunt deze JSON niet meer bewerken.
          </p>
        </div>
      );
  }
};
